import dayjs from 'dayjs'
import { TradingCalendarData } from '../proto/generated/trading-calendar'

/** Last weekday at 9:16 AM (excludes weekends). Used for simulator default timestamp. */
export const getLastWeekday = (): number => {
    const now = dayjs()
    let lastDay = now.subtract(1, 'day')
    if (lastDay.day() === 0) lastDay = lastDay.subtract(2, 'day')
    else if (lastDay.day() === 6) lastDay = lastDay.subtract(1, 'day')
    return lastDay.set('hour', 9).set('minute', 16).set('second', 0).set('millisecond', 0).valueOf()
}

export const getNextValidTradingDay = (date: Date, tradingCalendarData: TradingCalendarData | undefined): Date | null => {
    const holidays = tradingCalendarData?.holidays.map((h) => h.date) ?? []
    const specialTradingDays = tradingCalendarData?.specialTradingDays ?? []
    const dateStr = (d: Date) => d.toISOString().split('T')[0].replace(/-/g, '') // Convert to YYYYMMDD
    const isHoliday = (d: Date) => holidays.includes(dateStr(d))
    const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6
    const isSpecialTradingDay = (d: Date) => specialTradingDays.some((st) => st.date === dateStr(d) && st.sessionTypes.length > 0)
    let maxLookAhead = 10
    while (maxLookAhead-- > 0) {
        if (isSpecialTradingDay(date)) break
        if (!isHoliday(date) && !isWeekend(date)) break
        date.setDate(date.getDate() + 1)
    }
    if (maxLookAhead <= 0) return null
    date.setHours(0, 0, 0, 0)
    return new Date(date)
}

export const getPreviousValidTradingDay = (date: Date, tradingCalendarData: TradingCalendarData | undefined): Date | null => {
    const holidays = tradingCalendarData?.holidays.map((h) => h.date) ?? []
    const specialTradingDays = tradingCalendarData?.specialTradingDays ?? []
    const dateStr = (d: Date) => d.toISOString().split('T')[0].replace(/-/g, '') // Convert to YYYYMMDD
    const isHoliday = (d: Date) => holidays.includes(dateStr(d))
    const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6
    const isSpecialTradingDay = (d: Date) => specialTradingDays.some((st) => st.date === dateStr(d) && st.sessionTypes.length > 0)
    
    // Always go back at least one day first
    date.setDate(date.getDate() - 1)
    
    let maxLookBehind = 10
    while (maxLookBehind-- > 0) {
        if (isSpecialTradingDay(date)) break
        if (!isHoliday(date) && !isWeekend(date)) break
        date.setDate(date.getDate() - 1)
    }
    if (maxLookBehind <= 0) return null
    date.setHours(0, 0, 0, 0)
    return new Date(date)
}

/**
 * Get the effective trading date - returns today if it's a trading day,
 * otherwise returns the last valid trading day
 */
export const getEffectiveTradingDate = (tradingCalendarData: TradingCalendarData | undefined): Date => {
    // If no trading calendar data is available, use getLastWeekday as fallback
    if (!tradingCalendarData) {
        return new Date(getLastWeekday())
    }
    
    const today = new Date()
    const holidays = tradingCalendarData?.holidays.map((h) => h.date) ?? []
    const specialTradingDays = tradingCalendarData?.specialTradingDays ?? []
    const dateStr = (d: Date) => d.toISOString().split('T')[0].replace(/-/g, '') // Convert to YYYYMMDD
    const isHoliday = (d: Date) => holidays.includes(dateStr(d))
    const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6
    const isSpecialTradingDay = (d: Date) => specialTradingDays.some((st) => st.date === dateStr(d) && st.sessionTypes.length > 0)
    
    // If today is a special trading day, use today
    if (isSpecialTradingDay(today)) {
        today.setHours(0, 0, 0, 0)
        return today
    }
    
    // If today is not a holiday and not a weekend, use today
    if (!isHoliday(today) && !isWeekend(today)) {
        today.setHours(0, 0, 0, 0)
        return today
    }
    
    // Otherwise, get the previous valid trading day
    const previousTradingDay = getPreviousValidTradingDay(new Date(today), tradingCalendarData)
    return previousTradingDay || new Date(getLastWeekday()) // Fallback to getLastWeekday if no previous trading day found
}
